import React, { useCallback, useRef, useState } from "react";
import { X, UploadCloud, ImagePlus } from "lucide-react";

const MAX_IMAGES = 5;
const MAX_BYTES = 6 * 1024 * 1024; // 6MB per image
const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Uploader({ images, setImages, disabled }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback(
    async (files) => {
      const remaining = MAX_IMAGES - images.length;
      const accepted = Array.from(files)
        .filter((f) => ACCEPT.split(",").includes(f.type))
        .filter((f) => f.size <= MAX_BYTES)
        .slice(0, remaining);

      const next = [...images];
      for (const f of accepted) {
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await fileToBase64(f);
        next.push({
          id: `${f.name}-${f.size}-${Date.now()}`,
          name: f.name,
          dataUrl,
          base64: dataUrl.split(",")[1],
        });
      }
      setImages(next);
    },
    [images, setImages]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  };

  const remove = (id) =>
    setImages(images.filter((img) => img.id !== id));

  const full = images.length >= MAX_IMAGES;

  return (
    <div data-testid="uploader-root" className="w-full">
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !disabled && !full && inputRef.current?.click()}
        className={`relative cursor-pointer border border-dashed hairline transition-all duration-300 px-8 py-14 text-center bg-white ${
          dragging ? "border-[var(--brand-gold)] bg-[var(--brand-bg-2)]" : ""
        } ${disabled || full ? "cursor-not-allowed opacity-80" : "hover:border-[var(--brand-gold)]"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          disabled={disabled || full}
          data-testid="upload-input"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full flex items-center justify-center border hairline bg-[var(--brand-bg-2)]">
            <UploadCloud strokeWidth={1.5} className="w-6 h-6 text-[var(--brand-gold-dark)]" />
          </div>
          <p className="font-serif-display text-2xl text-[var(--brand-ink)]">
            {full ? "Maximal 5 Fotos erreicht" : "Ziehen Sie hier 1–5 Haarfotos hinein"}
          </p>
          <p className="text-sm text-[var(--brand-ink-soft)] max-w-md">
            Vorderansicht, Rückansicht und Nahaufnahmen von Längen und Kopfhaut ermöglichen die genaueste Beurteilung.
            JPG, PNG oder WEBP · bis zu 6 MB pro Foto.
          </p>
          {!full && (
            <button
              type="button"
              data-testid="upload-browse-btn"
              className="mt-2 inline-flex items-center gap-2 px-5 py-2 text-sm btn-outline-ink"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              disabled={disabled}
            >
              <ImagePlus className="w-4 h-4" strokeWidth={1.5} />
              Fotos auswählen
            </button>
          )}
        </div>
      </div>

      {images.length > 0 && (
        <div className="mt-6 grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="upload-thumbnails">
          {images.map((img, idx) => (
            <div
              key={img.id}
              className="group relative aspect-square overflow-hidden border hairline bg-white"
              data-testid={`upload-thumb-${idx}`}
            >
              <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <button
                type="button"
                onClick={() => remove(img.id)}
                disabled={disabled}
                data-testid={`upload-remove-${idx}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 text-[var(--brand-ink)] flex items-center justify-center hover:bg-[var(--brand-wine)] hover:text-white transition"
                aria-label="Foto entfernen"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
              <span className="absolute bottom-1 left-1 text-[10px] tracking-widest uppercase bg-white/85 px-1.5 py-0.5">
                {idx + 1}/{MAX_IMAGES}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
